import { useMutation, useQueryClient } from '@tanstack/react-query';

import ToastService from '~/services/toast.service';

import { i18n } from '~/utils';

import { queryKeysParsingCase } from '../queryKeys';

import { bulkRetryParsingCases } from './bulkRetryParsingCases';
import {
  type BulkRetryParsingCasesMutationOptions,
  type BulkRetryParsingCasesPayload,
  type BulkRetryParsingCasesResponse,
} from './types';

function notifyBulkRetryResult(response: BulkRetryParsingCasesResponse) {
  const { failed, requested, succeeded } = response;

  if (failed === 0) {
    ToastService.success(
      i18n.t(($) => $.bulkActions.retryResult.allSucceeded, {
        count: succeeded,
        ns: 'deliveryNoteReparsingV2',
      }),
    );
    return;
  }

  if (succeeded === 0) {
    ToastService.info(
      i18n.t(($) => $.bulkActions.retryResult.allFailed, {
        count: requested,
        ns: 'deliveryNoteReparsingV2',
      }),
    );
    return;
  }

  ToastService.info(
    i18n.t(($) => $.bulkActions.retryResult.partialFailure, {
      count: requested,
      failed,
      ns: 'deliveryNoteReparsingV2',
      succeeded,
    }),
  );
}

/**
 * Custom hook to bulk retry parsing cases from start (full pipeline per case).
 *
 * @param options - Optional react-query mutation options
 * @returns The mutation result
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Parsing-Cases
 */
export const useMutationBulkRetryParsingCases = (
  options?: BulkRetryParsingCasesMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: BulkRetryParsingCasesPayload) =>
      bulkRetryParsingCases(payload),
    onError() {
      return;
    },
    onSettled() {
      void queryClient.invalidateQueries({
        queryKey: [queryKeysParsingCase.base()],
      });
    },
    onSuccess(response) {
      notifyBulkRetryResult(response);
    },
    ...options,
  });
};
